import { isEthereumWallet } from '@dynamic-labs/ethereum'
import { Address, erc20Abi } from 'viem'
import { Wallet } from '@dynamic-labs/sdk-react-core'
import { GLUEX_ROUTER_URL } from '@/config/consts'

import { BaseUnitNumber } from '@/domain/types/NumericValues'
import { toBigInt } from '@/utils/bigNumber'
import { GetPriceError, GetPriceResult, MissingQuoteError, SwapError } from './types'
import { hypeAddressOverride } from './utils'
import { Token } from '@/domain/types/Token'
import { trackEvent } from '@/utils/fathom'
import { captureError } from 'instrumentation-client'
import { TokenSymbol } from '@/domain/types/TokenSymbol'

interface SwapProps {
  inputToken: Token
  outputToken: Token
  inputAmount: BaseUnitNumber
  priceData?: GetPriceResult | GetPriceError | null
  primaryWallet: Wallet | null
  chainId: number
  surgeAcknowledged: boolean
  nativeAssetSymbol: TokenSymbol
  setSwapState: (state: string) => void
  setSwapError: (error: boolean) => void
  setQuoteError: (error: boolean) => void
  onSuccess: () => void
}

// Values from GlueX SDK
const BASE_COMPUTATION_UNITS = 310000
const GAS_BUFFER = 1.4

export async function swap({
  inputToken,
  outputToken,
  inputAmount,
  priceData,
  primaryWallet,
  surgeAcknowledged,
  nativeAssetSymbol,
  setSwapState,
  setSwapError,
  setQuoteError,
  onSuccess,
}: SwapProps): Promise<void> {
  setSwapError(false)
  setQuoteError(false)
  setSwapState('Initializing...')

  if (!primaryWallet || !isEthereumWallet(primaryWallet) || !primaryWallet.address) {
    console.warn('Wallet not available or not an Ethereum wallet')
    setSwapState('')
    return
  }

  if (!priceData || !('result' in priceData)) {
    console.warn('Price data not available')
    setSwapState('')
    return
  }

  try {
    const gluexRouter = priceData.result.router
    const account = primaryWallet.address as Address
    const publicClient = await primaryWallet.getPublicClient()
    const walletClient = await primaryWallet.getWalletClient()

    setSwapState('Initializing...')

    if (inputToken.symbol !== nativeAssetSymbol) {
      const allowance = await publicClient.readContract({
        address: inputToken.address,
        abi: erc20Abi,
        functionName: 'allowance',
        args: [account, gluexRouter],
      })

      if (allowance < toBigInt(inputAmount)) {
        setSwapState('Approving...')
        const approvePermit2Tx = await walletClient.writeContract({
          address: inputToken.address,
          abi: erc20Abi,
          functionName: 'approve',
          args: [gluexRouter, toBigInt(inputAmount)],
        })
        await publicClient.waitForTransactionReceipt({ hash: approvePermit2Tx })
      }
    }

    setSwapState('Getting route...')

    const fetchOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': process.env.NEXT_PUBLIC_GLUEX_API_KEY || '',
      },
      body: JSON.stringify({
        inputToken: hypeAddressOverride(inputToken.address),
        outputToken: hypeAddressOverride(outputToken.address),
        inputAmount: toBigInt(inputAmount).toString(),
        userAddress: account,
        outputReceiver: account,
        chainID: 'hyperevm',
        uniquePID: process.env.NEXT_PUBLIC_GLUEX_UUID,
        surgeProtection: !surgeAcknowledged,
      }),
    }

    const response = await fetch(`${GLUEX_ROUTER_URL}/v1/quote`, fetchOptions)

    if (!response.ok) {
      throw new SwapError('Failed to generate quote', { response })
    }

    let returnData = await response.json()
    let retryCount = 0
    const maxRetries = 3

    while (returnData.statusCode.toString() !== '200' && retryCount < maxRetries) {
      setSwapState(`Retrying... (${retryCount + 1}/${maxRetries})`)
      retryCount++
      const retryResponse = await fetch(`${GLUEX_ROUTER_URL}/v1/quote`, fetchOptions)
      if (!retryResponse.ok) {
        continue
      }
      returnData = await retryResponse.json()
    }

    if (returnData.statusCode.toString() !== '200') {
      setQuoteError(true)
      throw new MissingQuoteError(returnData.error, returnData)
    }

    if (returnData.result.surgeProtectionApplied && !surgeAcknowledged) {
      throw new SwapError(
        'Surge protection was applied when obtaining final quote, but it was not acknowledged by the user',
        returnData,
      )
    }

    setSwapState('Awaiting confirmation...')

    const hash = await walletClient.sendTransaction({
      to: returnData.result.router,
      data: returnData.result.calldata,
      account,
      value: inputToken.symbol === nativeAssetSymbol ? toBigInt(inputAmount) : undefined,
      gas: toBigInt(Math.floor((returnData.result.computationUnits ?? BASE_COMPUTATION_UNITS) * GAS_BUFFER)),
    })

    setSwapState('Sending transaction...')

    const tx = await publicClient.waitForTransactionReceipt({ hash })

    if (tx.status !== 'success') {
      console.error('Transaction failed', tx)
      throw new SwapError('Transaction failed', tx)
    }
    trackEvent(`swap_success_${inputToken.symbol}_${outputToken.symbol}`)
    onSuccess()
  } catch (error) {
    if (error instanceof Error && error.message.includes('User rejected')) return

    if (!(error instanceof MissingQuoteError)) {
      trackEvent(`swap_error_${inputToken.symbol}_${outputToken.symbol}`)
    }

    if (error instanceof Error) {
      captureError(error, error instanceof SwapError ? error.details : undefined)
    }

    console.error('In swap: ', error)
    setSwapError(true)
  } finally {
    setSwapState('')
  }
}
